/**
 * Angebot · Einblicke (Migration 730) — der Abschnitt „Einblicke" im Angebots-Verlauf.
 *
 * Antoine, 19.10.2026: „zähle so viel wie möglich. Nimm alle Insights, die du bekommst, die legal
 * sind und zeige sie an." Gezeigt wird, was die Gastseite serverseitig protokolliert hat:
 * Zeitpunkt, Geräteklasse, Verweildauer, Lesefortschritt und die Stationen auf dem Weg zur Zusage.
 * Nicht erfasst (und deshalb nicht angezeigt): IP-Adresse, Browserkennung, Standort.
 *
 * Ruhige Darstellung (Regel „Ruhige Hinweise", 13.10.2026): grau, Symbol statt Farbe, kein Rot,
 * kein Orange. Diese Datei rechnet nichts - die Zahlen kommen fertig vom Server, formatiert wird
 * in `lib/einblicke.ts`.
 */
import { Icon } from "./components";
import {
  STATIONEN,
  dauerText,
  datumUhrzeit,
  geraetIcon,
  geraetLabel,
  versandZeile,
  type Einblicke,
} from "../lib/einblicke";

const LABEL = "font-label-md text-[11px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim";
const TEXT = "font-body-sm text-[13px] leading-relaxed text-on-background dark:text-inverse-on-surface";
const KLEIN = "font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim";

export function AngebotsEinblicke({ einblicke }: { einblicke: Einblicke | null | undefined }) {
  if (!einblicke) return null;
  const { aufrufe, proVersand, ereignisse } = einblicke;
  const zeitVon = (art: string) => ereignisse.find((e) => e.art === art)?.zeitpunkt ?? null;
  // Das Video steht nur da, wo es eines gibt - sonst wäre „—" eine Aussage über etwas, das es nicht gibt.
  const stationen = [
    ...STATIONEN,
    ...(zeitVon("video_abgespielt") ? [{ art: "video_abgespielt", label: "Video abgespielt", icon: "play_circle" }] : []),
  ];
  const versaende = [...proVersand].sort((a, b) => b.versandNr - a.versandNr);

  return (
    <section data-testid="angebots-einblicke" className="space-y-5">
      <h3 className={LABEL}>Einblicke</h3>

      {/* (a) Je Versand */}
      <div>
        <p className={`mb-1 ${KLEIN}`}>Aufrufe je Versand</p>
        {versaende.length === 0 ? (
          <p data-testid="einblicke-leer" className={KLEIN}>Noch kein Aufruf erfasst.</p>
        ) : (
          <ul className="divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
            {versaende.map((v) => (
              <li key={v.versandNr} data-testid="einblicke-versand" className={`flex items-start gap-2 py-2 ${TEXT}`}>
                <Icon name="mark_email_read" className="mt-0.5 shrink-0 text-[16px] text-on-surface-variant/70" />
                <span>{versandZeile(v)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* (b) Stationen */}
      <div>
        <p className={`mb-1 ${KLEIN}`}>Stationen auf der Angebotsseite (erstes Mal)</p>
        <ul className="divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
          {stationen.map((s) => {
            const z = datumUhrzeit(zeitVon(s.art));
            return (
              <li key={s.art} data-testid={`einblicke-station-${s.art}`} className={`flex items-center gap-2 py-2 ${TEXT}`}>
                <Icon name={s.icon} className="shrink-0 text-[16px] text-on-surface-variant/70" />
                <span className="flex-1">{s.label}</span>
                <span className={`tabular-nums ${z ? TEXT : KLEIN}`}>{z ?? "—"}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* (c) Letzte Aufrufe */}
      <div>
        <p className={`mb-1 ${KLEIN}`}>Letzte Aufrufe</p>
        {aufrufe.length === 0 ? (
          <p className={KLEIN}>Noch kein Aufruf erfasst.</p>
        ) : (
          <ul className="divide-y divide-[#F6F0E4] dark:divide-white/[0.05]">
            {aufrufe.map((a, i) => (
              <li
                key={`${a.zeitpunkt}-${i}`}
                data-testid="einblicke-aufruf"
                className={`grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-0.5 py-2 sm:grid-cols-[8.5rem_7rem_1fr_auto] ${TEXT}`}
              >
                <span className="tabular-nums">{datumUhrzeit(a.zeitpunkt)}</span>
                <span className="flex items-center gap-1">
                  <Icon name={geraetIcon(a.geraet)} className="shrink-0 text-[16px] text-on-surface-variant/70" />
                  {geraetLabel(a.geraet)}
                </span>
                {a.art === "pdf" ? (
                  <span className="flex items-center gap-1 sm:col-span-2">
                    <Icon name="picture_as_pdf" className="shrink-0 text-[16px] text-on-surface-variant/70" />
                    PDF geladen
                  </span>
                ) : (
                  <>
                    <span className={a.dauerSekunden === null ? KLEIN : ""}>
                      {a.dauerSekunden === null ? "Dauer —" : `Dauer ${dauerText(a.dauerSekunden)}`}
                    </span>
                    <span className={`tabular-nums ${a.scrollProzent === null ? KLEIN : ""}`}>
                      {a.scrollProzent === null ? "gelesen —" : `gelesen ${a.scrollProzent} %`}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className={KLEIN}>
        Erfasst werden nur Zeitpunkt, Geräteklasse, Verweildauer und Lesefortschritt, wenn der Gast
        den Link öffnet. Keine IP-Adresse, keine Browserkennung, kein Standort.
      </p>
    </section>
  );
}
